import { initDataReport } from '@utils/data-report';
import isServer from '@utils/env/is-server';
import { LayoutProps } from '@utils/layout-config';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import Head from 'next/head';
import { useEffect, useMemo } from 'react';
import { initBeacon } from '@utils/beacon';
import cookies from 'next-cookies';
import Layout from '../components/Layout';
import RouterBefore from './router-before';
import reportPerformance from '@utils/performance';
import { useRouter } from 'next/router';
import { getPageCacheTime } from '../worker/set-cache-flag';
import Error from 'src/pages/_error.page';
import '@tencent/tea-component/dist/tea.css';
import '@tencent/tpm-meeting-active/tea/tea.css';
import '../assets/styles/globals.scss';
import IS_DEV from '@utils/env/is-dev';
import { loginCheckUser, UserDataObject } from '@utils/login-util';
import resetQuestAnimationFrame from '@utils/browser/requestAnimationFrame';
import { NO_NEED_LOGIN_PAGES } from 'src/pages/auth-page-config';
import logo from '@assets/imgs/color-logo.png';
import 'src/i18n/index';
import { useEnableMeetingAppBack, disableWebviewBounds } from '@utils/browser/utils';
import { encryptUrl } from '@utils/log-util';
/**
 * 页面性能上报
 * @param metric
 */
export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (window.nextAegis) {
    const { pathname } = window.location;
    window.nextAegis.reportTime(`${pathname}_${metric.name}`, metric.value);
    window.nextAegis.infoAll(`web-vital_${pathname}_${metric.name}: ${metric.value}`);
  }
}

/**
 * 1200 以上：为桌面端
 * 1024~1200：为窄屏桌面端（侧边栏收起成一条）
 * 601~1024：为平板端（采用移动端的风格展示、局部模块根据平板进行调整；
 * 320~600
 */
const setRem = (size?: number) => `
 window.baseRemSize = ${size || 1};
 // 设置 rem 函数
 function setRem () {
   if (window.baseRemSize === -1) {
     document.documentElement.style.fontSize = 36 + "px";
     return;
   }
   var baseSize = 36;
   var vW = window.innerWidth; // 当前窗口的宽度
   var baseSet = 1440;
   if (vW < 1200 && vW > ${size || 1024}) {
     baseSet = 1200;
   }
   // 暂时平板和手机都用36px的，不改
   if (vW < ${size || 1024} || vW > 1440) {
     document.documentElement.style.fontSize = 36 + "px";
   } else {
     var basePc = baseSize / baseSet; // 表示1920的设计图,使用100PX的默认值
     var vH = window.innerHeight; // 当前窗口的高度
     vH = vH > 600 ? vH : 600;
     // 非正常屏幕下的尺寸换算
     let dueH = vW * 920 / baseSet;
     if (vH < dueH) { // 当前屏幕高度小于应有的屏幕高度，就需要根据当前屏幕高度重新计算屏幕宽度
       vW = vH * baseSet / 920;
     }
     let rem = vW * basePc; // 以默认比例值乘以当前窗口宽度,得到该宽度下的相应font-size值
     document.documentElement.style.fontSize = rem + "px";
   }
 }
 // 初始化
 setRem();
 // 改变窗口大小时重新设置 rem
 window.onresize = function () {
   setRem()
};`;

type SelfProps = {
  isMeetingApp: boolean;
  isLowIE: boolean;
  isMobile: boolean;
  iswx: boolean;
  userData: UserDataObject;
  isMiniprogram: Boolean;
};

function App({ Component, pageProps, ...others }: AppProps & SelfProps) {
  const router = useRouter();
  const { pathname } = router;
  const props = useMemo(() => LayoutProps[pathname] || {}, [pathname]);

  const isMobile = useMemo(() => {
    if (typeof window !== 'undefined') {
      return window.os.isMobile;
    }
    return true;
  }, []);

  useEffect(() => {
    window.__PAGE_LOADED__ = true; // 页面加载成功的标志
    const aegisWeb = async () => {
      const pageCacheTime = await getPageCacheTime(window.location.href);
      const fromCache = pageCacheTime > 0;
      window.nextAegis.setConfig({
        ext1: fromCache ? 'cache' : 'network', // 页面内容来自 service worker 缓存
      });
      reportPerformance({ fromEntry: (router.query.from as string) || '', pageCacheTime });
      // 访问日志上报
      window.nextAegis.reportEvent('pv');
      const reportData = {
        // 上报日志需要脱敏
        url: encryptUrl(window.location.href),
        sw: {
          scriptURL: window.navigator?.serviceWorker?.controller?.scriptURL,
          state: window.navigator?.serviceWorker?.controller?.state,
          pageCacheTime,
        },
      };
      window.nextAegis.infoAll(`[visitInfo]-${JSON.stringify(reportData)}`);
      // TODO: 需要去掉
      if (window.AegisErrs) {
        window.AegisErrs.forEach((item: any) => window.nextAegis.infoAll(item));
      }
    };
    if (!IS_DEV) {
      aegisWeb();
    }
    if (typeof wemeet !== 'undefined') {
      // 隐藏app自带的底部难看的导航条
      wemeet.ui.setBottomNavBarStatus({ status: 2 });
    } else if (others.isMeetingApp) {
      window.nextAegis?.infoAll('failed to set header');
    }
    console.log(`%cVERSION: ${BUILD_TIME}`, 'color:#fff;font-size:14px;background:blue');
    resetQuestAnimationFrame();
  }, []);

  useEffect(() => {
    if (pathname === '/quick-buy/[eventId]') {
      window.baseRemSize = -1;
    } else {
      window.baseRemSize = 1;
      window.setRem?.();
    }
  }, [pathname]);

  useEnableMeetingAppBack();
  disableWebviewBounds();

  if (!isServer) {
    // 灯塔初始化
    initBeacon();
    initDataReport();
  }

  if (others.isLowIE) {
    return <Error statusCode={10001} />;
  }

  return (
    <>
      <Head>
        <title key="page-title">
          {/* eslint-disable-next-line no-nested-ternary */}
          {!isMobile ? '活动管理后台' : others.isMiniprogram ? '' : '活动空间'}
        </title>
        <link rel="shortcut icon" href={logo} />
        <meta name="referrer" content="origin" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,maximum-scale=1,minimum-scale=1,user-scalable=0,viewport-fit=cover"
        />
        {others.isMeetingApp ? (
          <script src="https://cdn.meeting.tencent.com/jssdk/wemeet-js-sdk-1.0.90.js" />
        ) : null}
        <script
          dangerouslySetInnerHTML={{
            // @ts-ignore
            __html: setRem(others.pageSize),
          }}
        ></script>
      </Head>
      <RouterBefore isMobile={others.isMobile} userData={others.userData}>
        <Layout
          {...props}
          iswx={others.iswx}
          ismeetingApp={others.isMeetingApp}
          isMiniprogram={others.isMiniprogram}
        >
          {/* @ts-ignore */}
          <Component {...pageProps} />
        </Layout>
      </RouterBefore>
    </>
  );
}

App.getInitialProps = async ({ ctx }: any) => {
  const ua = ctx.req?.headers['user-agent'] || navigator.userAgent || '';
  const isMeetingApp = !!ua.match(/app\/tencent_wemeet/);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isMiniprogram = ua.toLowerCase().indexOf('miniprogram') > -1;
  const isLowIE = ua.indexOf('MSIE') > -1;
  const { pathname = '', req } = ctx;
  let size = 1024;
  // let cookie;
  let userData = { userStatus: {}, userInfo: {} };
  if (pathname === '/event/[eventid]/registration') size = 768;
  if (pathname === '/quick-buy/[eventId]') size = -1;
  if (NO_NEED_LOGIN_PAGES.indexOf(pathname) === -1) {
    // 不需要登录态的页面可以不获取，减少耗时影响
    // 服务端获取登录状态
    // 获取当前已登陆用户权限
    const cookie = cookies(ctx);
    userData = await loginCheckUser(cookie, req ? ctx : undefined);
  }
  return {
    isMeetingApp,
    isLowIE,
    pageSize: size,
    isMobile,
    iswx: /micromessenger/i.test(ua),
    userData,
    isMiniprogram,
  };
};

export default App;
